import { TONE_BADGE } from '../utils/statusColors';
import React, { useState } from 'react';

interface CollapsibleSectionProps {
  icon: React.ReactNode;
  title: string;
  /** Linha de resumo mostrada com a seção fechada. É o que evita ter que abrir para saber o estado. */
  summary: string;
  /** Selo à direita do título (ex.: "Ativo", "1 registrada"). */
  badge?: { text: string; tone: 'ok' | 'neutro' | 'alerta' };
  /** Abre já expandida. Use quando algo exige ação. */
  defaultOpen?: boolean;
  /**
   * 'lista' (padrão) é a faixa de borda a borda do modal de perfil. 'cartao' é
   * para modal cujo corpo já tem fundo escuro e campos em `bg-slate-900`: sem a
   * moldura do cartão, esses campos somem dentro do próprio fundo.
   */
  variante?: 'lista' | 'cartao';
  children: React.ReactNode;
}

// Selos do resumo pelo mapa de tons (14/10/2026), não verde e âmbar avulsos.
const TONS = {
  ok: TONE_BADGE.success,
  neutro: TONE_BADGE.neutral,
  alerta: TONE_BADGE.warning,
} as const;

/**
 * Bloco recolhível do modal de perfil.
 *
 * Fechado, mostra ícone, título, selo de estado e uma linha de resumo — o
 * suficiente para o usuário saber se precisa abrir. Antes, todas as seções
 * ficavam expandidas ao mesmo tempo: o formulário de senha ocupava o topo
 * (sendo a ação mais rara), o botão destrutivo de desativar MFA aparecia de
 * cara, e as passkeys ficavam abaixo da dobra, invisíveis sem rolagem.
 */
export default function CollapsibleSection({
  icon,
  title,
  summary,
  badge,
  defaultOpen = false,
  variante = 'lista',
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const cartao = variante === 'cartao';

  return (
    // 'cartao' no tom da casca (14/10/2026): era bg-slate-800 (azul-marinho) sobre o bg-central-cartao do modal.
    <div className={cartao ? 'bg-central-elevado rounded-lg border border-central-borda' : 'border-t border-slate-800'} data-secao-cartao={cartao || undefined}>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 text-left transition-colors ${cartao ? 'px-4 py-3 rounded-lg hover:bg-white/5' : 'px-6 py-4 hover:bg-slate-800/40'}`}
      >
        <span className='text-slate-400 shrink-0'>{icon}</span>

        <span className='flex-1 min-w-0'>
          <span className='flex items-center gap-2'>
            <span className='text-sm font-semibold text-slate-200'>{title}</span>
            {badge && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONS[badge.tone]}`}>
                {badge.text}
              </span>
            )}
          </span>
          <span className='block text-xs text-slate-500 truncate mt-0.5'>{summary}</span>
        </span>

        <svg
          className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
        </svg>
      </button>

      {open && <div className={cartao ? 'px-4 pb-4 space-y-4' : 'px-6 pb-6 space-y-4'}>{children}</div>}
    </div>
  );
}
